import { AlertCircleIcon, CheckCircle2Icon, LoaderIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Spinner,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useIsMobile,
} from '@dotfile/frontend/shared/design-system';
import { ClientPortalCase_ContactCase } from '@dotfile/shared/data-access-client-portal';
import { CaseStatusEnum } from '@dotfile/shared/domain';

import { FullContainer, useContactCases } from '../../shared';
import { CaseEntityList, CaseTabItem } from './components';
import { CaseHeader } from './components/header';

export const OnboardingsPage = () => {
  const { data, loading } = useContactCases();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const { pendingActions, underReview, completed } = useMemo(() => {
    return (data?.contactCases ?? []).reduce(
      (acc, caseEntity) => {
        if (
          caseEntity.status === CaseStatusEnum.draft ||
          (caseEntity.status === CaseStatusEnum.open &&
            caseEntity.contactHasActions)
        ) {
          return {
            ...acc,
            pendingActions: [...acc.pendingActions, caseEntity],
          };
        } else if (
          caseEntity.status === CaseStatusEnum.open &&
          !caseEntity.contactHasActions
        ) {
          return {
            ...acc,
            underReview: [...acc.underReview, caseEntity],
          };
        } else if (caseEntity.status === CaseStatusEnum.approved) {
          return {
            ...acc,
            completed: [...acc.completed, caseEntity],
          };
        }

        return acc;
      },
      {
        pendingActions: [],
        underReview: [],
        completed: [],
      } as Record<
        'pendingActions' | 'underReview' | 'completed',
        ClientPortalCase_ContactCase[]
      >,
    );
  }, [data?.contactCases]);

  return (
    <FullContainer>
      <CaseHeader />
      {loading ? (
        <Spinner />
      ) : (
        <Tabs mt="2" isLazy>
          <TabList
            height="10"
            borderBottom="2px"
            overflowX={isMobile ? 'auto' : 'hidden'}
            overflowY="hidden"
            borderColor="gray.200"
          >
            {pendingActions.length !== 0 && (
              <CaseTabItem
                icon={AlertCircleIcon}
                label={t('checks.tabs.pending_action.title', {
                  ns: 'client-portal',
                  defaultValue: 'Pending actions',
                })}
                badgeColorScheme="orange"
                badgeCount={pendingActions.length}
              />
            )}
            {underReview.length !== 0 && (
              <CaseTabItem
                icon={LoaderIcon}
                label={t('checks.tabs.under_review.title', {
                  ns: 'client-portal',
                  defaultValue: 'Under review',
                })}
                badgeCount={underReview.length}
              />
            )}
            {completed.length !== 0 && (
              <CaseTabItem
                icon={CheckCircle2Icon}
                label={t('checks.tabs.completed.title', {
                  ns: 'client-portal',
                  defaultValue: 'Completed',
                })}
                badgeCount={completed.length}
              />
            )}
          </TabList>

          <TabPanels mt={{ base: 2, md: 6 }}>
            {[pendingActions, underReview, completed].map(
              (cases, index) =>
                cases.length !== 0 && (
                  <TabPanel key={index} px="0" pb="0">
                    <CaseEntityList cases={cases} />
                  </TabPanel>
                ),
            )}
          </TabPanels>
        </Tabs>
      )}
    </FullContainer>
  );
};
